/**
 * Hide Resident Dialog Component
 *
 * Dialog for hiding a resident from the Care Queue for a specified number of days.
 * Features a custom teal header matching the Add Note dialog pattern.
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { XCircle, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useApiService } from "../../contexts/ApiServiceContext";
import type { ResidentSummary } from "../../types/residents";
import { trackCareQueueEvent } from "../../lib/tracking";

interface HideResidentDialogProps {
  resident: ResidentSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HideResidentDialog({
  resident,
  open,
  onOpenChange,
}: HideResidentDialogProps) {
  const api = useApiService();
  const queryClient = useQueryClient();

  const [days, setDays] = useState("7");
  const [error, setError] = useState<string | null>(null);

  // Analytics: emit when dialog opens
  useEffect(() => {
    if (open) {
      trackCareQueueEvent("care_queue_dialog_opened", { dialogType: "hide" });
    }
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      trackCareQueueEvent("care_queue_dialog_action", {
        dialogType: "hide",
        action: "cancel",
      });
      setDays("7");
      setError(null);
    }
    onOpenChange(newOpen);
  };

  const hideMutation = useMutation({
    mutationFn: async () => {
      if (!resident) throw new Error("No resident selected");
      return api.hideResident(resident.resident_id, {
        days: parseInt(days, 10),
      }, resident.wq_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].includes("residents"),
      });
      toast.success(
        `${resident?.resident_name} hidden for ${days} day${days !== "1" ? "s" : ""}`
      );
      handleOpenChange(false);
    },
    onError: (err) => {
      const message =
        err instanceof Error ? err.message : "Failed to hide resident";
      setError(message);
      toast.error("Failed to hide resident", { description: message });
    },
  });

  const handleSubmit = () => {
    const parsedDays = parseInt(days, 10);
    if (isNaN(parsedDays) || parsedDays < 1) {
      setError("Please enter a valid number of days (1 or more)");
      return;
    }
    if (parsedDays > 365) {
      setError("Maximum hide duration is 365 days");
      return;
    }
    setError(null);
    trackCareQueueEvent("care_queue_dialog_action", {
      dialogType: "hide",
      action: "confirm",
      hideDays: parsedDays,
    });
    hideMutation.mutate();
  };

  if (!resident) {
    return null;
  }

  const displayRoom = resident.room || "No Room";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden bg-card [&>button]:hidden"
        style={{ width: "560px", maxWidth: "calc(100vw - 2rem)" }}
      >
        {/* Custom Teal Header */}
        <div className="px-8 py-5 relative rounded-t bg-primary">
          <button
            onClick={() => handleOpenChange(false)}
            className="absolute top-5 right-5 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
            aria-label="Close"
          >
            <XCircle className="h-6 w-6" />
          </button>
          <h2
            className="text-primary-foreground"
            style={{ fontSize: "20px", fontWeight: 700 }}
          >
            Hide Resident
          </h2>
          <p className="text-base text-primary-foreground/90 mt-1">
            {resident.resident_name} &bull; {displayRoom}
          </p>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-5 bg-card">
          {/* Days Input */}
          <div className="space-y-2">
            <label
              htmlFor="hide-days"
              className="text-sm font-medium text-foreground"
            >
              Hide for how many days?
            </label>
            <Input
              id="hide-days"
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => {
                setDays(e.target.value);
                if (error) setError(null);
              }}
              style={{ border: "1px solid rgba(209, 210, 219, 1.00)" }}
              aria-invalid={!!error}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          {/* Note Section */}
          <div className="p-4 rounded bg-background">
            <p className="text-sm text-foreground">
              <span className="font-medium">Note:</span> This resident will be
              hidden from the Care Queue for the specified number of days.
            </p>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={hideMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={hideMutation.isPending || !days.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <EyeOff className="h-4 w-4 mr-2" />
            {hideMutation.isPending ? "Hiding..." : "Hide Resident"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
